/**
 * V4AIBuilder — Phase 7b.
 *
 * Universal AI surface for the V4 canvas. Three modes share one
 * conversation thread:
 *
 *   • BUILD    — prompt-to-workflow generation. Calls aiCanvasBuild
 *                with the current canvas + prompt; renders a diff
 *                preview the operator confirms with Apply.
 *   • DEBUG    — diagnose a failed run + suggest fixes. Calls
 *                aiCanvasDebug. Renders fix chips the operator clicks
 *                to apply individually.
 *   • IMPORT   — paste Conductor / canvas JSON. Validates shape +
 *                renders the same diff preview path as Build.
 *
 * Improvements over V3's AIWorkflowChatBar:
 *   1. Mode tabs vs V3's conflated single input.
 *   2. Diff preview BEFORE apply (V3 just re-loads, leaving operators
 *      guessing what changed).
 *   3. ⌘I hotkey + collapsed chip when idle (V3 had no hotkey + always
 *      occupied the canvas footer).
 *   4. Per-workflow chat persistence in localStorage (V3 resets per
 *      page load).
 *   5. Auto-primes Debug mode when openRunId points to a FAILED run
 *      (V3 requires the operator to figure out they need Debug).
 *   6. Full fix-chip rail vs V3's "3 + N more in chat" overflow.
 *
 * Phase 7b-1 ships UI shell ONLY — no API calls. Each subsequent
 * sub-phase wires one mode. This lets us verify the surface area
 * before risking any canvas mutation.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, X, Send, ChevronDown, ChevronUp, AlertTriangle, FileJson, Wand2 } from 'lucide-react';

const MODES = [
  { key: 'build',  label: 'Build',  icon: Wand2,
    hint: 'Describe the workflow and I\'ll generate it.' },
  { key: 'debug',  label: 'Debug',  icon: AlertTriangle,
    hint: 'I\'ll analyze the failed run and suggest fixes.' },
  { key: 'import', label: 'Import', icon: FileJson,
    hint: 'Paste a Conductor or V4 canvas JSON to import.' },
];

// localStorage scope per-workflow so chats don't leak across workflows.
function chatStorageKey(workflowId) {
  return workflowId ? `v4ai:chat:${workflowId}` : null;
}

export default function V4AIBuilder({
  // Controlled open state (host owns the toggle so ⌘I can flip it
  // from anywhere in CanvasPageV4).
  open,
  onOpenChange,
  /* Phase 7c-3 — when rendered inside V4RightRail, skip the collapsed
   * chip + floating chrome. The rail owns positioning + visibility. */
  embedded = false,
  // Workflow context — needed to scope chat persistence + to know
  // whether we have a canvas to operate on at all.
  workflowId,
  workflowName,
  // Auto-prime: when this is set and points to a FAILED run, we open
  // in Debug mode + pre-populate the prompt with a diagnose request.
  failedRunId,
  // Phase 7b-2/3/4 will pass these in. For now they're placeholders.
  onSubmitBuild,        // (prompt) => Promise<{ summary, addCount, dropCount, warnings? }>
  onSubmitDebug,        // (prompt) => Promise<{ summary, healthEmoji, quickFixes[], warnings? }>
  onSubmitImport,       // (jsonText) => Promise<{ summary, addCount, dropCount }>
  // readOnly — when the workflow is LOCKED/ARCHIVED we still show the
  // panel but disable apply buttons.
  readOnly = false,
}) {
  const [mode, setMode] = useState('build');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  // Conversation history: [{role: 'user'|'ai', text, kind?: 'error'|'diff'|'fix-rail', payload?}]
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // ── Persistence: hydrate on workflow change, save on messages change.
  useEffect(() => {
    const key = chatStorageKey(workflowId);
    if (!key) { setMessages([]); return; }
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setMessages(parsed.slice(-50));
        else setMessages([]);
      } else {
        setMessages([]);
      }
    } catch (_) {
      setMessages([]);
    }
  }, [workflowId]);

  useEffect(() => {
    const key = chatStorageKey(workflowId);
    if (!key) return;
    try {
      // Cap at 50 messages to avoid unbounded growth + slow localStorage.
      // Strip non-serializable kinds (diff/fix-rail) since their payloads
      // carry function callbacks — rehydrating them would resurrect dead
      // closures pointing at unmounted canvas references. We replace the
      // entry with a plain text trace so the conversation still reads
      // sensibly after reload.
      const tail = messages.slice(-50).map((m) => {
        if (m.kind === 'diff' || m.kind === 'fix-rail') {
          return { ts: m.ts, role: m.role, text: m.text || '(action — applied or discarded)' };
        }
        return m;
      });
      window.localStorage.setItem(key, JSON.stringify(tail));
    } catch (_) { /* quota exceeded — drop silently */ }
  }, [messages, workflowId]);

  // ── Auto-prime Debug mode when a failed run id arrives.
  useEffect(() => {
    if (!failedRunId || !open) return;
    setMode('debug');
    setPrompt(`Diagnose run ${String(failedRunId).slice(0, 8)}: explain what went wrong + suggest fixes.`);
  }, [failedRunId, open]);

  // Auto-focus input when the panel opens.
  useEffect(() => {
    if (open && inputRef.current) {
      // Defer to next frame so the input is mounted.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const activeMode = useMemo(
    () => MODES.find((m) => m.key === mode) || MODES[0],
    [mode],
  );

  const pushMessage = (msg) => setMessages((prev) => [...prev, { ts: Date.now(), ...msg }]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const trimmed = prompt.trim();
    if (!trimmed || busy) return;
    pushMessage({ role: 'user', text: trimmed });
    setPrompt('');
    setBusy(true);
    try {
      let result;
      if (mode === 'build' && typeof onSubmitBuild === 'function') {
        result = await onSubmitBuild(trimmed);
      } else if (mode === 'debug' && typeof onSubmitDebug === 'function') {
        result = await onSubmitDebug(trimmed);
      } else if (mode === 'import' && typeof onSubmitImport === 'function') {
        result = await onSubmitImport(trimmed);
      } else {
        // Phase 7b-1 placeholder — no handler wired yet. Reply with a
        // diagnostic stub so the operator can see the round-trip is
        // structurally wired.
        result = {
          summary: `(${activeMode.label} mode handler not yet wired in Phase 7b-1 — coming in 7b-2/3/4.)`,
        };
      }
      pushMessage({ role: 'ai', text: result?.summary || '(no response)', kind: result?.kind, payload: result?.payload });
    } catch (err) {
      pushMessage({
        role: 'ai',
        kind: 'error',
        text: `Sorry — ${err?.message || String(err)}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    const key = chatStorageKey(workflowId);
    if (key) { try { window.localStorage.removeItem(key); } catch (_) {} }
  };

  // ── Collapsed chip ─────────────────────────────────────────────
  // When embedded in the right rail, the rail owns visibility — skip
  // the floating chip entirely (rail is always present).
  if (!open && embedded) {
    return null;
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onOpenChange && onOpenChange(true)}
        title="Open AI Builder (⌘I)"
        aria-label="Open AI Builder"
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          zIndex: 7,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px',
          fontSize: 12, fontWeight: 600,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0a766b 100%)',
          border: 'none', borderRadius: 999,
          cursor: 'pointer',
          boxShadow: '0 8px 24px -6px rgba(124, 58, 237, 0.45)',
          fontFamily: 'inherit',
        }}
      >
        <Sparkles size={13} aria-hidden="true" />
        AI Builder
        <kbd style={{ ...kbdStyle, background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}>⌘I</kbd>
      </button>
    );
  }

  // ── Expanded panel ─────────────────────────────────────────────
  return (
    <aside
      role="region"
      aria-label="AI Builder"
      style={embedded ? {
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        background: '#ffffff',
        fontFamily: 'inherit',
        overflow: 'hidden',
      } : {
        position: 'absolute',
        bottom: 12,
        right: 12,
        width: 440,
        maxHeight: 'min(560px, calc(100% - 24px))',
        display: 'flex', flexDirection: 'column',
        background: '#ffffff',
        border: '0.5px solid #e2e8f0',
        borderRadius: 10,
        boxShadow: '0 18px 42px -12px rgba(15, 23, 42, 0.28), 0 4px 10px -2px rgba(15, 23, 42, 0.12)',
        zIndex: 11, // above RunDetailPanel (z=9) + ValidationPanel (z=8)
        fontFamily: 'inherit',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px',
        borderBottom: '0.5px solid #e2e8f0',
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.06) 0%, rgba(37, 99, 235, 0.06) 100%)',
      }}>
        <Sparkles size={13} aria-hidden="true" style={{ color: '#0d9488' }} />
        <span style={{
          fontSize: 11.5, fontWeight: 700, color: '#0f172a',
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          AI Builder
        </span>
        {workflowName && (
          <span style={{
            fontSize: 10.5, color: '#64748b',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: 180,
          }}>
            · {workflowName}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {messages.length > 0 && (
          <button
            type="button"
            onClick={clearChat}
            title="Clear chat history"
            style={iconBtnStyle}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={() => onOpenChange && onOpenChange(false)}
          aria-label="Close AI Builder"
          title="Close (Esc)"
          style={iconBtnStyle}
        >
          <X size={12} aria-hidden="true" />
        </button>
      </div>

      {/* Mode tabs */}
      <div role="tablist" style={{
        display: 'flex', alignItems: 'center', gap: 0,
        padding: '0 8px',
        borderBottom: '0.5px solid #e2e8f0',
      }}>
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => setMode(m.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '8px 10px',
                fontSize: 11, fontWeight: 600,
                color: active ? '#0f172a' : '#64748b',
                background: 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid #0d9488' : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <Icon size={11} aria-hidden="true" />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Conversation body */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, minHeight: 200,
          overflowY: 'auto',
          padding: 12,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        {messages.length === 0 && (
          <div style={{
            margin: 'auto', textAlign: 'center',
            fontSize: 11.5, color: '#64748b', lineHeight: 1.5,
            maxWidth: 320,
          }}>
            <Sparkles size={20} aria-hidden="true" style={{ color: '#cbd5e1', marginBottom: 8 }} />
            <div style={{ fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              {activeMode.label} mode
            </div>
            <div>{activeMode.hint}</div>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble
            key={i}
            msg={m}
            onMutate={(evt) => {
              // Re-render trigger: append a tiny status footer message so
              // the operator sees a confirmation in the conversation
              // history. Doesn't mutate canvas — that's already happened
              // inside the bubble's apply/discard handler.
              if (evt?.kind === 'error') {
                pushMessage({ role: 'ai', kind: 'error', text: `Couldn't apply: ${evt.error}` });
              }
            }}
            onPatchMsg={(patch) => {
              // Bugfix #127 — bubbles lift their applied/discarded /
              // doneFixIds state up here so it survives close+reopen.
              setMessages((prev) => prev.map((m2, j) => j === i ? { ...m2, ...patch } : m2));
            }}
          />
        ))}
        {busy && (
          <div style={{
            alignSelf: 'flex-start',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 10px',
            fontSize: 11, color: '#64748b',
            background: '#f1f5f9',
            borderRadius: 8,
          }}>
            <Sparkles size={11} aria-hidden="true" className="v4-spin" />
            Thinking…
          </div>
        )}
      </div>

      {/* Input row */}
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex', alignItems: 'flex-end', gap: 6,
          padding: 10,
          borderTop: '0.5px solid #e2e8f0',
          background: '#f8fafc',
        }}
      >
        <textarea
          ref={inputRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            // ⌘↵ / Ctrl+↵ submits. Plain Enter inserts a newline, like
            // Slack / ChatGPT.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder={modePlaceholder(mode)}
          disabled={busy || !workflowId || readOnly}
          rows={2}
          style={{
            flex: 1, resize: 'none',
            padding: '6px 8px',
            fontSize: 12, color: '#0f172a',
            background: '#ffffff',
            border: '0.5px solid #cbd5e1',
            borderRadius: 6,
            outline: 'none',
            fontFamily: mode === 'import'
              ? 'SFMono-Regular, Menlo, monospace'
              : 'inherit',
          }}
        />
        <button
          type="submit"
          disabled={busy || !prompt.trim() || !workflowId || readOnly}
          title="Send (⌘↵)"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 34, height: 34, padding: 0,
            color: '#ffffff',
            background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0a766b 100%)',
            border: 'none', borderRadius: 6,
            cursor: (busy || !prompt.trim() || !workflowId || readOnly) ? 'not-allowed' : 'pointer',
            opacity: (busy || !prompt.trim() || !workflowId || readOnly) ? 0.5 : 1,
            fontFamily: 'inherit',
          }}
        >
          <Send size={13} aria-hidden="true" />
        </button>
      </form>
    </aside>
  );
}

/* ───── single conversation bubble ──────────────────────────────────
 * Renders four shapes:
 *
 *   • user        — purple gradient, right-aligned
 *   • ai/text     — slate, left-aligned (default)
 *   • ai/error    — red bg, left-aligned
 *   • ai/diff     — special preview card with addCount/dropCount stats,
 *                    warnings list, Apply + Discard buttons. Set by
 *                    Phase 7b-2/4 when a build/import result is ready
 *                    to materialize onto the canvas.
 *   • ai/fix-rail — Phase 7b-3 fix chips. Each chip is a one-click
 *                    apply that calls payload.applyFix(fixId).
 *
 * Diff + fix-rail payloads carry their action callbacks directly so
 * the bubble doesn't need to know about canvas internals — keeps this
 * component pure UI.
 * ─────────────────────────────────────────────────────────────────── */
function MessageBubble({ msg, onMutate, onPatchMsg }) {
  const isUser = msg.role === 'user';
  const isError = msg.kind === 'error';

  if (msg.kind === 'diff' && msg.payload) {
    return (
      <DiffBubble
        msg={msg}
        onMutate={onMutate}
        onSetDiffState={(s) => onPatchMsg && onPatchMsg({ diffState: s })}
      />
    );
  }
  if (msg.kind === 'fix-rail' && msg.payload) {
    return (
      <FixRailBubble
        msg={msg}
        onMutate={onMutate}
        onSetDoneFixes={(arr) => onPatchMsg && onPatchMsg({ doneFixIds: arr })}
      />
    );
  }

  return (
    <div style={{
      alignSelf: isUser ? 'flex-end' : 'flex-start',
      maxWidth: '85%',
      padding: '7px 10px',
      fontSize: 12, lineHeight: 1.5,
      color: isError ? '#7f1d1d' : (isUser ? '#ffffff' : '#0f172a'),
      background: isError
        ? '#fef2f2'
        : (isUser
          ? 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0a766b 100%)'
          : '#f1f5f9'),
      border: isError ? '0.5px solid #fecaca' : 'none',
      borderRadius: 8,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}>
      {msg.text}
    </div>
  );
}

/* ───── diff preview bubble (Phase 7b-2/4) ────────────────────────── */
function DiffBubble({ msg, onMutate, onSetDiffState }) {
  // State is LIFTED to the message itself (msg.diffState) so it
  // survives close+reopen of the panel. Without this lift the bubble
  // remounts with the local useState reset to 'pending' and the Apply
  // buttons reappear — a click then double-mutates the canvas.
  // Bugfix #127.
  const state = msg.diffState || 'pending'; // pending | applied | discarded
  const p = msg.payload || {};
  const addCount  = p.addCount  ?? 0;
  const dropCount = p.dropCount ?? 0;
  const keepCount = p.keepCount ?? 0;
  const warnings  = Array.isArray(p.warnings) ? p.warnings : [];

  const setState = (next) => {
    if (typeof onSetDiffState === 'function') onSetDiffState(next);
  };

  const doApply = async () => {
    if (state !== 'pending' || typeof p.apply !== 'function') return;
    try {
      await p.apply();
      setState('applied');
      if (typeof onMutate === 'function') onMutate({ kind: 'applied' });
    } catch (e) {
      // Stay pending so the operator can retry (don't lift to error
      // state — error toast already surfaced via onMutate).
      if (typeof onMutate === 'function') onMutate({ kind: 'error', error: e?.message || String(e) });
    }
  };
  const doDiscard = () => {
    if (state !== 'pending') return;
    setState('discarded');
    if (typeof p.discard === 'function') p.discard();
    if (typeof onMutate === 'function') onMutate({ kind: 'discarded' });
  };

  return (
    <div style={{
      alignSelf: 'flex-start',
      maxWidth: '95%',
      padding: 10,
      fontSize: 12, lineHeight: 1.5,
      color: '#0f172a',
      background: '#ffffff',
      border: '0.5px solid #cbd5e1',
      borderRadius: 8,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {msg.text && (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <StatChip label="add"  count={addCount}  tone="add" />
        <StatChip label="keep" count={keepCount} tone="keep" />
        <StatChip label="drop" count={dropCount} tone="drop" />
      </div>
      {warnings.length > 0 && (
        <div style={{
          padding: '6px 8px',
          background: '#fffbeb',
          border: '0.5px solid #fcd34d',
          borderRadius: 6,
          fontSize: 11, color: '#78350f',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>Warnings:</div>
          {warnings.slice(0, 5).map((w, i) => (
            <div key={i}>• {typeof w === 'string' ? w : (w.message || JSON.stringify(w))}</div>
          ))}
          {warnings.length > 5 && (
            <div style={{ marginTop: 2, color: '#92400e' }}>+{warnings.length - 5} more</div>
          )}
        </div>
      )}
      {state === 'pending' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            onClick={doApply}
            style={{ ...primaryBtnStyle, flex: 1 }}
          >
            Apply to canvas
          </button>
          <button
            type="button"
            onClick={doDiscard}
            style={secondaryBtnStyle}
          >
            Discard
          </button>
        </div>
      )}
      {state === 'applied' && (
        <div style={{ fontSize: 11, color: '#065f46', fontWeight: 600 }}>
          ✓ Applied to canvas
        </div>
      )}
      {state === 'discarded' && (
        <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
          Discarded
        </div>
      )}
    </div>
  );
}

/* ───── fix-rail bubble (Phase 7b-3) ──────────────────────────────── */
function FixRailBubble({ msg, onMutate, onSetDoneFixes }) {
  // doneIds lifted into msg.doneFixIds (array, serializes to localStorage)
  // for the same close+reopen reason as DiffBubble (bugfix #127). busyId
  // stays local — it's a transient "in-flight" indicator and never
  // survives unmount.
  const doneIds = useMemo(
    () => new Set(Array.isArray(msg.doneFixIds) ? msg.doneFixIds : []),
    [msg.doneFixIds],
  );
  const [busyId, setBusyId] = useState(null);
  const p = msg.payload || {};
  const fixes = Array.isArray(p.fixes) ? p.fixes : [];
  const health = p.health;

  const applyFix = async (fix) => {
    if (busyId || doneIds.has(fix.id)) return;
    setBusyId(fix.id);
    try {
      await (typeof p.applyFix === 'function' ? p.applyFix(fix) : Promise.resolve());
      if (typeof onSetDoneFixes === 'function') {
        const next = [...doneIds, fix.id];
        onSetDoneFixes(next);
      }
      if (typeof onMutate === 'function') onMutate({ kind: 'fix-applied', fixId: fix.id });
    } catch (e) {
      if (typeof onMutate === 'function') onMutate({ kind: 'error', error: e?.message || String(e) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      alignSelf: 'flex-start',
      maxWidth: '95%',
      padding: 10,
      fontSize: 12, lineHeight: 1.5,
      color: '#0f172a',
      background: '#ffffff',
      border: '0.5px solid #cbd5e1',
      borderRadius: 8,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {msg.text && (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</div>
      )}
      {health && (
        <div style={{
          fontSize: 11, fontWeight: 600, color: '#0f172a',
          padding: '4px 8px',
          background: '#f1f5f9',
          borderRadius: 6,
          alignSelf: 'flex-start',
        }}>
          {health}
        </div>
      )}
      {fixes.length === 0 ? (
        <div style={{ fontSize: 11, color: '#64748b' }}>No quick-fixes suggested.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {fixes.map((f) => {
            const done = doneIds.has(f.id);
            const busy = busyId === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => applyFix(f)}
                disabled={busy || done}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 6,
                  padding: '6px 10px',
                  fontSize: 11.5, color: done ? '#94a3b8' : '#0f172a',
                  background: done ? '#f1f5f9' : '#ffffff',
                  border: '0.5px solid ' + (done ? '#e2e8f0' : '#cbd5e1'),
                  borderRadius: 6,
                  cursor: (busy || done) ? 'default' : 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  textDecoration: done ? 'line-through' : 'none',
                }}
              >
                <span style={{ flex: 1 }}>{f.label || f.message || f.id}</span>
                <span style={{
                  fontSize: 10, color: done ? '#94a3b8' : (busy ? '#0ea5e9' : '#64748b'),
                  fontWeight: 600,
                }}>
                  {done ? 'done' : (busy ? 'applying…' : 'apply')}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatChip({ label, count, tone }) {
  const palette = {
    add:  { bg: '#d1fae5', fg: '#065f46' },
    keep: { bg: '#dbeafe', fg: '#1e40af' },
    drop: { bg: '#fee2e2', fg: '#991b1b' },
  };
  const p = palette[tone] || palette.keep;
  return (
    <span style={{
      padding: '2px 8px',
      fontSize: 10.5, fontWeight: 600,
      color: p.fg, background: p.bg,
      borderRadius: 999,
    }}>
      {count} {label}
    </span>
  );
}

const primaryBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '6px 12px',
  fontSize: 11.5, fontWeight: 600,
  color: '#ffffff',
  background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0a766b 100%)',
  border: 'none', borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const secondaryBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '6px 12px',
  fontSize: 11.5, fontWeight: 500,
  color: '#475569',
  background: '#f1f5f9',
  border: '0.5px solid #cbd5e1',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/* ───── helpers ───────────────────────────────────────────────────── */
function modePlaceholder(mode) {
  if (mode === 'build')  return 'Describe the workflow you want…  (⌘↵ to send)';
  if (mode === 'debug')  return 'Ask about the failed run…  (⌘↵ to send)';
  if (mode === 'import') return 'Paste Conductor or canvas JSON here…  (⌘↵ to import)';
  return 'Type your message…';
}

const iconBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '3px 8px',
  fontSize: 10.5, fontWeight: 600,
  color: '#475569',
  background: '#f1f5f9',
  border: '0.5px solid #e2e8f0',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const kbdStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '1px 5px',
  marginLeft: 4,
  fontSize: 9.5, fontWeight: 600,
  color: '#475569', background: '#e2e8f0',
  border: 'none', borderRadius: 3,
  fontFamily: 'inherit',
  lineHeight: 1.4,
};
